import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useMealDeliveries } from '@/hooks/useMealDeliveries';
import { useServiceLogs } from '@/hooks/useServiceLogs';
import { format, isWithinInterval } from 'date-fns';
import { BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';


interface Props {
  rangeStart: Date;
  rangeEnd: Date;
  siteId?: string | null;
}

const MEAL_KEYS = ['breakfast', 'lunch', 'dinner', 'milk', 'juice'] as const;
type MealKey = (typeof MEAL_KEYS)[number];

export function MealAnalyticsSection({ rangeStart, rangeEnd, siteId }: Props) {
  const { deliveries } = useMealDeliveries(siteId ?? undefined, { start: rangeStart, end: rangeEnd });
  const { serviceLogs } = useServiceLogs();

  const rangeDeliveries = deliveries; // already date-bounded by hook

  // Distributed counts per (date, meal)
  const distributedMap = useMemo(() => {
    const map = new Map<string, Record<MealKey, number>>();
    (serviceLogs as any[]).forEach((log) => {
      const t = (log.service_type || '').toLowerCase();
      if (!MEAL_KEYS.includes(t as MealKey)) return;
      if (!log.created_at) return;
      const ts = new Date(log.created_at);
      if (!isWithinInterval(ts, { start: rangeStart, end: rangeEnd })) return;
      const dateKey = format(ts, 'yyyy-MM-dd');
      if (!map.has(dateKey)) map.set(dateKey, { breakfast: 0, lunch: 0, dinner: 0, milk: 0, juice: 0 });
      map.get(dateKey)![t as MealKey] += log.quantity || 1;
    });
    return map;
  }, [serviceLogs, rangeStart, rangeEnd]);

  // Aggregate totals per meal
  const totals = useMemo(() => {
    const t: Record<MealKey, { received: number; distributed: number }> = {
      breakfast: { received: 0, distributed: 0 },
      lunch: { received: 0, distributed: 0 },
      dinner: { received: 0, distributed: 0 },
      milk: { received: 0, distributed: 0 },
      juice: { received: 0, distributed: 0 },
    };
    rangeDeliveries.forEach((d) => {
      t[d.meal_type].received += d.quantity_received || 0;
    });
    distributedMap.forEach((rec) => {
      MEAL_KEYS.forEach((k) => (t[k].distributed += rec[k]));
    });
    return t;
  }, [rangeDeliveries, distributedMap]);

  const chartData = MEAL_KEYS.map((k) => ({
    name: k.charAt(0).toUpperCase() + k.slice(1),
    Distributed: totals[k].distributed,
    Discarded: Math.max(0, totals[k].received - totals[k].distributed),
  }));

  // Top menus leaderboard
  const menuStats = useMemo(() => {
    const map = new Map<string, { count: number; received: number; distributed: number; meal: MealKey }>();
    rangeDeliveries.forEach((d) => {
      const menu = (d.menu_description || '').trim();
      if (!menu) return;
      const distRec = distributedMap.get(d.service_date);
      const dist = distRec ? distRec[d.meal_type] : 0;
      const key = `${d.meal_type}::${menu.toLowerCase()}`;
      const prev = map.get(key) ?? { count: 0, received: 0, distributed: 0, meal: d.meal_type };
      prev.count += 1;
      prev.received += d.quantity_received;
      prev.distributed += dist;
      map.set(key, prev);
    });
    return Array.from(map.entries())
      .map(([key, v]) => {
        const menu = key.split('::').slice(1).join('::');
        const rate = v.received > 0 ? Math.round((v.distributed / v.received) * 100) : 0;
        return { menu, ...v, rate };
      })
      .sort((a, b) => b.rate - a.rate || b.count - a.count)
      .slice(0, 10);
  }, [rangeDeliveries, distributedMap]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-lg">Meal Efficiency (Distributed vs Discarded)</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="Distributed" stackId="a" fill="hsl(var(--primary))" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Discarded" stackId="a" fill="hsl(var(--discard))" fillOpacity={0.25} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">Top Items (by distribution rate)</CardTitle></CardHeader>
        <CardContent>
          {menuStats.length === 0 ? (
            <p className="text-sm text-muted-foreground">No menu data recorded for this range.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Times Served</TableHead>
                  <TableHead className="text-right">Received</TableHead>
                  <TableHead className="text-right">Distributed</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {menuStats.map((m, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium">{m.menu}</TableCell>
                    <TableCell className="capitalize">{m.meal}</TableCell>
                    <TableCell className="text-right">{m.count}</TableCell>
                    <TableCell className="text-right">{m.received}</TableCell>
                    <TableCell className="text-right">{m.distributed}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant={m.rate >= 80 ? 'default' : 'secondary'}>{m.rate}%</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
